import { useRef, useState } from 'react';
import { useTheme } from '../../../hooks/useTheme';
import { dentalScreens } from '../../../mocks/dental';
import { dentalAsset } from '../asset';

export default function DentalScreens() {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const accentText = isLight ? '#0f766e' : '#14b8a6';
  const [active, setActive] = useState(0);
  /* La primera captura queda diferida (está bajo el pliegue); en cuanto el
     usuario elige una pestaña, la imagen se carga de inmediato. */
  const [touched, setTouched] = useState(false);
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const selectTab = (i: number) => {
    setTouched(true);
    setActive(i);
  };

  const ink = isLight ? '#0a1628' : '#ffffff';
  const sub = isLight ? 'rgba(10,22,40,0.72)' : 'rgba(255,255,255,0.55)';
  const border = isLight ? 'rgba(10,22,40,0.09)' : 'rgba(255,255,255,0.07)';
  const screen = dentalScreens[active];

  /* Flechas para moverse entre pestañas (patrón WAI-ARIA de tabs) */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next =
      e.key === 'ArrowRight'
        ? (active + 1) % dentalScreens.length
        : (active - 1 + dentalScreens.length) % dentalScreens.length;
    selectTab(next);
    tabsRef.current[next]?.focus();
  };

  return (
    <section
      id="sistema-odontologia"
      className="py-14 md:py-20 px-4 sm:px-6 scroll-mt-24"
      style={{
        background: isLight ? '#f6fafd' : '#071b2e',
        borderTop: `1px solid ${border}`,
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-10">
          <span
            className="inline-block text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2.5"
            style={{ color: isLight ? '#0e7490' : '#22d3ee' }}
          >
            El sistema por dentro
          </span>
          <h2
            className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-3"
            style={{ color: ink }}
          >
            Así se ve <span style={{ color: accentText }}>InnoFlex Odontología</span>
          </h2>
          <p className="font-body text-sm md:text-base max-w-2xl mx-auto" style={{ color: sub }}>
            Pantallas reales del sistema. Los datos de pacientes y doctores fueron sustituidos por
            información de demostración.
          </p>
        </div>

        {/* Pestañas */}
        <div
          role="tablist"
          aria-label="Pantallas de InnoFlex Odontología"
          onKeyDown={onKeyDown}
          className="flex gap-2 mb-6 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible sm:flex-wrap sm:justify-center"
        >
          {dentalScreens.map((s, i) => {
            const selected = i === active;
            return (
              <button
                key={s.id}
                ref={(el) => {
                  tabsRef.current[i] = el;
                }}
                role="tab"
                type="button"
                id={`tab-${s.id}`}
                aria-selected={selected}
                aria-controls={`panel-screen-${s.id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => selectTab(i)}
                className="flex flex-shrink-0 items-center gap-2 px-4 py-2 rounded-full border font-body text-xs sm:text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  backgroundColor: selected
                    ? 'rgba(6,182,212,0.14)'
                    : isLight
                      ? '#ffffff'
                      : 'rgba(255,255,255,0.04)',
                  borderColor: selected
                    ? 'rgba(6,182,212,0.45)'
                    : isLight
                      ? 'rgba(10,22,40,0.10)'
                      : 'rgba(255,255,255,0.09)',
                  color: selected ? (isLight ? '#0e7490' : '#22d3ee') : sub,
                  transform: selected ? 'translateY(-1px)' : 'translateY(0)',
                }}
              >
                <i className={`${s.icon} text-sm`} aria-hidden="true" />
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Captura */}
        <div
          role="tabpanel"
          id={`panel-screen-${screen.id}`}
          aria-labelledby={`tab-${screen.id}`}
          className="max-w-5xl mx-auto"
        >
          <figure
            className="rounded-2xl border overflow-hidden"
            style={{
              borderColor: isLight ? 'rgba(10,22,40,0.12)' : 'rgba(255,255,255,0.10)',
              background: isLight ? '#ffffff' : 'rgba(4,16,31,0.85)',
              boxShadow: isLight
                ? '0 18px 50px rgba(6,182,212,0.14), 0 4px 16px rgba(10,22,40,0.08)'
                : '0 24px 64px rgba(0,0,0,0.50)',
            }}
          >
            {/* Barra de ventana */}
            <div
              className="flex items-center gap-2 px-4 py-2.5 border-b"
              style={{
                borderColor: isLight ? 'rgba(10,22,40,0.08)' : 'rgba(255,255,255,0.07)',
                background: isLight ? '#eef4f9' : 'rgba(255,255,255,0.03)',
              }}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#f472b6' }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#fbbf24' }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#14b8a6' }} />
              <span
                className="font-body text-[11px] ml-2 truncate"
                style={{ color: isLight ? 'rgba(10,22,40,0.62)' : 'rgba(255,255,255,0.55)' }}
              >
                InnoFlex Dental — {screen.label}
              </span>
            </div>

            <img
              key={screen.id}
              src={dentalAsset(screen.src)}
              width={screen.width}
              height={screen.height}
              alt={screen.alt}
              loading={touched ? 'eager' : 'lazy'}
              decoding="async"
              className="w-full h-auto block"
            />

            <figcaption
              className="px-5 py-4 font-body text-xs sm:text-sm leading-relaxed border-t"
              style={{
                color: sub,
                borderColor: isLight ? 'rgba(10,22,40,0.08)' : 'rgba(255,255,255,0.07)',
              }}
            >
              {screen.caption}
            </figcaption>
          </figure>
        </div>
      </div>
    </section>
  );
}
